import React, { useContext } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import { getLocationParts } from './utils';
import { LocationSeparator, TopText, ViewOnMapButton } from './styled';

type LocationTextProps = {
  dealCard: DealCard;
  isOverlay: boolean;
};

const Sep = () => <LocationSeparator>·</LocationSeparator>;

const LocationText = ({ dealCard, isOverlay }: LocationTextProps) => {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { listView } = useContext(SearchPageContext);
  const { city, distance } = getLocationParts(dealCard, localization, t);
  const viewOnMapLabel = t('vacation.oss.components.searchresults.tour_tile.view_on_map');
  const hotelId = dealCard?.product?.accommodation?.hotelId;
  const { handleViewMapClick } = useSearchDealsPageData();

  if (isOverlay) return null;

  // mobile · list view → distance itself is the map trigger
  if (isMobile && listView) {
    return (
      <TopText variant="BodySmall" color="textSecondary">
        {city}
        <Sep />
        <ViewOnMapButton data-view-on-map={hotelId} onClick={(e) => handleViewMapClick?.(e, hotelId)}>
          {distance}
        </ViewOnMapButton>
      </TopText>
    );
  }

  // mobile · map view → plain location, no map action
  if (isMobile && !listView) {
    return (
      <TopText variant="BodySmall" color="textSecondary">
        {city}
        <Sep />
        {distance}
      </TopText>
    );
  }

  // desktop · list view → explicit "view on map" button after location
  if (!isMobile && listView) {
    return (
      <TopText variant="BodySmall" color="textSecondary">
        {city}
        <Sep />
        {distance}
        <Sep />
        <ViewOnMapButton data-view-on-map={hotelId} onClick={(e) => handleViewMapClick?.(e, hotelId)}>
          {viewOnMapLabel}
        </ViewOnMapButton>
      </TopText>
    );
  }

  // desktop · map view → plain location, no map action
  return (
    <TopText variant="BodySmall" color="textSecondary">
      {city}
      <Sep />
      {distance}
    </TopText>
  );
};

export default LocationText;
